import {
  AppBar,
  Toolbar,
  Box,
  InputBase,
  IconButton,
  Stack,
  Typography,
  Paper,
  Badge,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";

import MenuDrawer from "./MenuDrawer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { fetchCategories } from "../../api/catalogApi";

function normalizeCategories(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.results)) return raw.results;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.categories)) return raw.categories;
  if (Array.isArray(raw.meta?.categories)) return raw.meta.categories;
  return [];
}

function getChildren(cat) {
  if (Array.isArray(cat?.subcategories) && cat.subcategories.length > 0)
    return cat.subcategories;
  if (Array.isArray(cat?.children) && cat.children.length > 0)
    return cat.children;
  return [];
}

function getTitle(cat) {
  return cat?.title || cat?.name || cat?.slug || "بدون عنوان";
}

const DrawerCategoryItem = ({ cat, level = 0, onSelectCategory, onClose }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const children = getChildren(cat);
  const hasChildren = children.length > 0;

  const handleSelect = (e) => {
    if (e) e.stopPropagation();

    if (onSelectCategory) {
      onSelectCategory(cat.slug);
    } else {
      navigate(`/category/${cat.slug}`);
    }

    if (onClose) onClose();
  };

  const handleToggle = (e) => {
    if (e) e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <>
      <ListItemButton
        onClick={hasChildren ? handleToggle : handleSelect}
        sx={{
          py: 1,
          pl: 1.5,
          pr: 1.5 + level * 1.8,
          borderRadius: 2,
          mb: 0.5,
          flexDirection: "row",
          justifyContent: "space-between",
          textAlign: "right",
          bgcolor: level > 0 ? "grey.50" : "transparent",
          "&:hover": { bgcolor: "grey.100" },
        }}
      >
        <Box
          onClick={handleSelect}
          sx={{ flex: 1, minWidth: 0, cursor: "pointer", textAlign: "right" }}
        >
          <ListItemText
            primary={getTitle(cat)}
            primaryTypographyProps={{
              fontSize: level > 0 ? "0.83rem" : "0.88rem",
              fontWeight: level > 0 ? 400 : 500,
              color: "text.primary",
              textAlign: "right",
            }}
          />
        </Box>
        {hasChildren && (
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ p: 0.3, ml: 1, color: "grey.600" }}
            aria-label={open ? "بستن زیرمجموعه" : "باز کردن زیرمجموعه"}
          >
            {open ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowLeftIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              borderRight: "2px solid",
              borderColor: "grey.100",
              mr: 2.2,
              pr: 0.5,
            }}
          >
            <List component="div" disablePadding sx={{ p: 0, m: 0 }}>
              {children.map((sub) => (
                <DrawerCategoryItem
                  key={sub.slug || sub.id || sub.title}
                  cat={sub}
                  level={level + 1}
                  onSelectCategory={onSelectCategory}
                  onClose={onClose}
                />
              ))}
            </List>
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default function HeaderUI({
  search,
  setSearch,
  handleSearch,
  handleClearSearch,
  handleNavigation,
  cartCount,
  logoSrc = "/zomioo_logo2.jpeg",

  categories = [],
  onSelectCategory,
  isAuthenticated = false,
  user = null,
  onLogout,
}) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoriesDrawerOpen, setCategoriesDrawerOpen] = useState(false);
  const [fetchedCats, setFetchedCats] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);

  const propCategories = useMemo(
    () => normalizeCategories(categories),
    [categories],
  );

  useEffect(() => {
    if (!categoriesDrawerOpen) return;
    if (propCategories.length > 0) return;

    let cancelled = false;
    const load = async () => {
      setLoadingCats(true);
      try {
        const data = await fetchCategories();
        if (!cancelled) setFetchedCats(normalizeCategories(data));
      } catch (err) {
        console.error("Categories drawer fetch failed", err);
        if (!cancelled) setFetchedCats([]);
      } finally {
        if (!cancelled) setLoadingCats(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [categoriesDrawerOpen, propCategories.length]);

  const visibleCategories =
    propCategories.length > 0 ? propCategories : fetchedCats;

  const navigate = useNavigate();

  const handleCategorySelect = (slug) => {
  if (!slug) return;

  setCategoriesDrawerOpen(false);

  if (onSelectCategory) {
    onSelectCategory(slug);
  } else {
    navigate(`/category/${slug}`);
  }

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

  return (
    <>
      {/* 1. TOP HEADER */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#ffffff",
          color: "text.primary",
          direction: "rtl",
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 2, md: 4 },
            py: { xs: 1, md: 1.5 },
            position: "relative",
          }}
        >
          {/* RIGHT SIDE: Menu Icon */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              color: "text.secondary",
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: "50px",
              p: { xs: 0.8, md: 1 },
              "&:hover": { color: "text.primary", borderColor: "grey.300" },
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" } }} />
          </IconButton>

          {/* CENTER: Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: isSearchOpen ? { xs: "none", sm: "flex" } : "flex",
              alignItems: "center",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
            }}
          >
            <img
              src={logoSrc}
              alt="Zomioo"
              style={{
                maxHeight: 32,
                height: "100%",
                objectFit: "contain",
                width: "auto",
                maxWidth: "90px",
              }}
            />
          </Box>

          {/* LEFT SIDE: Expandable Search */}
          <Box sx={{ zIndex: 2 }}>
            {isSearchOpen ? (
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch();
                }}
                sx={{
                  width: { xs: "180px", sm: "240px", md: "280px" },
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "grey.100",
                  borderRadius: "50px",
                  height: { xs: 38, md: 42 },
                  px: { xs: 1.5, md: 2 },
                  border: "1px solid",
                  borderColor: "grey.300",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
                }}
              >
                <InputBase
                  placeholder="جستجوی محصول"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  sx={{
                    flex: 1,
                    color: "text.primary",
                    fontSize: { xs: "0.8rem", md: "0.9rem" },
                  }}
                />
                <IconButton
                  onClick={() => {
                    setIsSearchOpen(false);
                    handleClearSearch();
                  }}
                  sx={{ p: 0.5, color: "grey.500" }}
                >
                  <CloseIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
                <IconButton type="submit" sx={{ p: 0.5, color: "grey.500" }}>
                  <SearchIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Box>
            ) : (
              <IconButton
                onClick={() => setIsSearchOpen(true)}
                sx={{
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: "50px",
                  p: { xs: 0.8, md: 1 },
                  "&:hover": { color: "#ef4056", borderColor: "grey.300" },
                }}
              >
                <SearchIcon sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" } }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* 2. BOTTOM NAVBAR (mobile only, stuck to bottom) */}
      <Paper
        elevation={3}
        sx={{
          display: { xs: "block", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          m: 0,
          width: "auto",
          maxWidth: "100vw",
          boxSizing: "border-box",
          overflowX: "hidden",
          borderRadius: "16px 16px 0 0",
          bgcolor: "#ffffff",
          pt: 1,
          pb: "calc(8px + env(safe-area-inset-bottom, 0px))",
          px: 0,
          boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.08)",
          borderTop: "1px solid",
          borderColor: "grey.200",
          direction: "rtl",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-around"
          alignItems="center"
          sx={{
            width: "100%",
            maxWidth: 500,
            mx: "auto",
            px: 1,
            boxSizing: "border-box",
          }}
        >
          {/* 1. Home (خانه) */}
          <Box
            component={Link}
            to="/"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: "grey.700",
              textDecoration: "none",
              "&:hover": { color: "text.primary" },
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: "1.6rem" }} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.72rem", mt: 0.3, fontWeight: 500 }}
            >
              خانه
            </Typography>
          </Box>

          {/* 3. Categories (دسته‌ها) */}
          <Box
            onClick={() => setCategoriesDrawerOpen(true)}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: "grey.700",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
          >
            <GridViewOutlinedIcon sx={{ fontSize: "1.6rem" }} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.72rem", mt: 0.3, fontWeight: 500 }}
            >
              دسته‌ها
            </Typography>
          </Box>

          {/* 4. Cart (سبد خرید) */}
          {/* <Box
            component={Link}
            to="/cart"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: "grey.700",
              textDecoration: "none",
              "&:hover": { color: "text.primary" },
            }}
          >
            <Badge
              badgeContent={cartCount}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "#ef4056",
                  color: "#fff",
                  fontSize: "0.65rem",
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <ShoppingCartOutlinedIcon sx={{ fontSize: "1.6rem" }} />
            </Badge>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.72rem", mt: 0.3, fontWeight: 500 }}
            >
              سبد خرید
            </Typography>
          </Box> */}

          {/* 5. Account / Profile (حساب کاربری) */}
          <Box
            onClick={() => handleNavigation("profile")}
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: "grey.700",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: "1.6rem" }} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.72rem", mt: 0.3, fontWeight: 500 }}
            >
              حساب کاربری
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Categories Bottom Drawer (Mobile) */}
      <Drawer
        anchor="bottom"
        open={categoriesDrawerOpen}
        onClose={() => setCategoriesDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: "75vh",
            p: 2,
            direction: "rtl",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "1.05rem", fontWeight: 700 }}
          >
            دسته‌بندی محصولات
          </Typography>
          <IconButton
            onClick={() => setCategoriesDrawerOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        <List disablePadding>
          {loadingCats ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 2,
              }}
            >
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                در حال بارگذاری دسته‌بندی‌ها...
              </Typography>
            </Box>
          ) : visibleCategories.length > 0 ? (
            visibleCategories.map((cat) => (
              <DrawerCategoryItem
                key={cat.slug || cat.id || cat.title}
                cat={cat}
                onSelectCategory={handleCategorySelect}
                onClose={() => setCategoriesDrawerOpen(false)}
              />
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 1, textAlign: "right" }}
            >
              دسته‌بندی یافت نشد.
            </Typography>
          )}
        </List>
      </Drawer>

      {/* 3. SIDE DRAWER MENU */}
      <MenuDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        categories={categories}
        onSelectCategory={onSelectCategory || handleCategorySelect}
        handleNavigation={handleNavigation}
        cartCount={cartCount}
      />
    </>
  );
}
